import * as React from 'react';

import {
  SIZE_SCALE,
  PADDING_X,
  PADDING_Y,
  TYPE_SCALE,
  MARGIN_Y,
  COLOR_SCALE,
  FONT_WEIGHTS,
  TRANSFORM_SCALE,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className={`relative ${SIZE_SCALE.full} overflow-auto`}>
      <table
        ref={ref}
        className={cn(`${SIZE_SCALE.full} caption-bottom ${TYPE_SCALE.sm}`, className)}
        {...props}
      />
    </div>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      `border-t ${COLOR_SCALE['surface-muted']}/50 ${FONT_WEIGHTS.medium} [&>tr]:last:border-b-0`,
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        `border-b transition-colors hover:${COLOR_SCALE['surface-muted']}/50 data-[state=selected]:${COLOR_SCALE['surface-muted']}`,
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      `${SIZE_SCALE['10']} ${PADDING_X['2']} text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:${TRANSFORM_SCALE['translate-y']['2px']}`,
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      `${PADDING_X['2']} ${PADDING_Y['2']} align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:${TRANSFORM_SCALE['translate-y']['2px']}`,
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn(
      `${MARGIN_Y['4']} ${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`,
      className
    )}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
